import { IAnimationControllerConfiguration, Inject, ModelEvents } from "@denishussar/html5-game-engine";
import { AnimationContents } from "../../../AnimationContents";
import { ButtonController, IButtonControllerOptions } from "../../Common/ButtonController";
import { GreenButtonTextStyle } from "../../Constants/TextStyles";
import { LocalizationContents } from "../../LocalizationContents";
import { GameLogic } from "../../Logic/GameLogic";
import { SavesManager } from "../../Managers/SavesManager";
import { BoosterNameEnum, BoosterTypeEnum, GameModel, GameStateEnum, InstrumentTypeEnum } from '../../Models/GameModel';
import { BoosterListItemController } from "../UI/BoosterListItemController";
import { FTUEArrowController } from "./FTUEArrowController";
import { FTUEPopupController, IFTUEPopupControllerOptions } from "./FTUEPopupController";
import { FTUEPopupEndController } from "./FTUEPopupEndController";
import { FTUEPopupMainController, IFTUEPopupMainControllerOptions } from "./FTUEPopupMainController";
import { GameScreenController, IGameScreenControllerOptions } from "./GameScreenController";
import {
    GameEvent,
    GameEventPopupButtonName,
    GameEventPopupName,
    GameEventSceneButtonClick,
} from '../../Constants/GameEvent';
import { Graphics, Point, Sprite, Texture } from "pixi.js";
import gsap from 'gsap';
import isMobile from 'is-mobile';

import * as filter from '@pixi/filter-glow';
import {PaletteListController} from "../UI/PaletteListController";

const BOOSTER_HIT_AREA = 200;
const PALETTE_ITEM_HIT_AREA = 140;

export class FTUEGameScreenController extends GameScreenController {
    public constructor(
        @Inject options: IGameScreenControllerOptions,
        @Inject gameLogic: GameLogic,
        @Inject saveManager: SavesManager,
        @Inject modelEvents: ModelEvents,
        @Inject model: GameModel,
    ) {
        super(options, gameLogic, saveManager, modelEvents, model);
    }

    private overlayShowingTime: number = 0.15;
    private _width: number;
    private _height: number;
    private _scale: number;

    private btnNeedleLocker: Sprite;
    private btnBucketLocker: Sprite;
    private btnBallLocker: Sprite;
    private paletteLockers: Sprite[];

    private isDestroyed = false;

    protected getConfiguration(): IAnimationControllerConfiguration {
        const config = super.getConfiguration();
        config.modelWatch  = config.modelWatch ? config.modelWatch : {};
        config.modelWatch.palette = () => {
            this.setupPaletteLockers();
        }

        config.childControllers.topBarMenu.options.hideExitButtonInSettings = true;

        return config;
    }

    protected onInitialize(): void {
        super.onInitialize();

        this.minusButton.visible = false;
        this.plusButton.visible = false;
        this.topBarMenu.coins.visible = false;

        this.btnBallLocker = new Sprite(Texture.EMPTY);
        this.btnBallLocker.width = BOOSTER_HIT_AREA;
        this.btnBallLocker.height = BOOSTER_HIT_AREA;
        this.display.addChild(this.btnBallLocker);

        this.btnNeedleLocker = new Sprite(Texture.EMPTY);
        this.btnNeedleLocker.width = BOOSTER_HIT_AREA;
        this.btnNeedleLocker.height = BOOSTER_HIT_AREA;
        this.display.addChild(this.btnNeedleLocker);

        this.btnBucketLocker = new Sprite(Texture.EMPTY);
        this.btnBucketLocker.width = BOOSTER_HIT_AREA;
        this.btnBucketLocker.height = BOOSTER_HIT_AREA;
        this.display.addChild(this.btnBucketLocker);

        this.paletteLockers = [];
        if (this.$model.palette.length > 0) {
            this.setupPaletteLockers();
        }

        console.dir(this);

        this.setupSkipButton();

        this.viewportController.lockUserActions(true);

        setTimeout(() => {
            this.startTutorial();
        }, 100);
    }

    protected onDestroy(): void {
        this.isDestroyed = true;
    }

    protected onResize(width: number, height: number, scale: number): void {
        super.onResize(width, height, scale);

        this._width = width;
        this._height = height;
        this._scale = scale;

        const btnReset = this.$childControllers.get('SkipButton') as ButtonController;
        if (btnReset && btnReset.display) {
            const topLeftPoint = this.display.toLocal(new Point(0, 0), this.renderer.stage);
            const topRightPoint = this.display.toLocal(new Point(width, 0), this.renderer.stage);
            btnReset.display.position.set(topLeftPoint.x + 200 , topLeftPoint.y + 100);

            if (isMobile()) {
                btnReset.display.position.set(topRightPoint.x - 475 , topLeftPoint.y + 100);
            }
        }

        const bottomLeftPoint = this.display.toLocal(new Point(0, height), this.renderer.stage);
        const topLeftPoint = this.display.toLocal(new Point(0, 0), this.renderer.stage);
        for (let i = 0; i < this.paletteLockers.length; i++) {
            const sprite = this.paletteLockers[i];

            if (isMobile()) {
                sprite.position.set(topLeftPoint.x + 300, topLeftPoint.y + 130 + i * 130);
            } else {
                sprite.position.set(bottomLeftPoint.x + 60 + i * 130, bottomLeftPoint.y - 160);
            }
        }

        const btnBall = this.btns_booster.$childControllers.get('btn_ball') as BoosterListItemController;
        this.btnBallLocker.position.set((this.btns_booster.x + btnBall.x - this.btnBallLocker.width / 2), (this.btns_booster.y + btnBall.y - this.btnBallLocker.height / 2));

        const btnNeedle = this.btns_booster.$childControllers.get('btn_needle') as BoosterListItemController;
        this.btnNeedleLocker.position.set((this.btns_booster.x + btnNeedle.x - this.btnNeedleLocker.width / 2), (this.btns_booster.y + btnNeedle.y - this.btnNeedleLocker.height / 2));

        const btnBucket = this.btns_booster.$childControllers.get('btn_bucket') as BoosterListItemController;
        this.btnBucketLocker.position.set((this.btns_booster.x + btnBucket.x - this.btnNeedleLocker.width / 2), (this.btns_booster.y + btnBucket.y - this.btnBucketLocker.height / 2));

        this.updateOverlay();
        this.updatePopupPosition();
    }

    private settings = {
        lines: 0,
        balls: 0,
        boosterBallPartOfPictureWidth: 0
    }

    private async startTutorial() {
        this.setFTUEModelSettings();

        this.modelEvents.dispatch(this.$model, GameEvent.tutorialStart);

        let level = this.saveManager.getSaveGameData().tutorialStep === undefined ? 0 : this.saveManager.getSaveGameData().tutorialStep;

        const order = [
            () => { return this.step01_showPrompt()},
            () => { this.saveManager.saveTutorialStep(1); return this.step02_pointJoistick()},
            () => { this.saveManager.saveTutorialStep(2); return this.step03_pointColor()},
            () => { return this.step04_waitColored()},
            () => { return this.timeout(1000)},
            () => { this.saveManager.saveTutorialStep(5); return this.step05_pointBoosterLine()},
            () => { return this.step06_waitBoosterLine()},
            () => { this.saveManager.saveTutorialStep(7); return this.step07_pointBoosterBall()},
            () => { return this.step08_waitBoosterBall()},
            () => { return this.timeout(1000)},
            () => { this.saveManager.saveTutorialStep(10); return this.step09_showLastPopup()},
            () => { return this.step10_waitPaintEnding()}
        ];

        try {
            for (let i = level; i < order.length; i++) {
                await order[i]();
            }
        } catch (error) {
            // TODO это чтоб ловить ошибки из промисов, ЕСЛИ выйти из тутора и снова войти
        }

        this.modelEvents.dispatch(this.$model, GameEvent.tutorialComplete, {
            stitchCount: this.model.filledPixelsCount,
            dragCount: this.model.dragCount,
            zoomInCount: this.model.zoomInCount,
            zoomOutCount: this.model.zoomOutCount,
        });
    }

    private setFTUEModelSettings(): void {
        this.settings.lines = this.gameLogic.getBoosterCount(BoosterTypeEnum.needle);
        for (let i = 0; i < this.settings.lines - 1; i++) {
            this.gameLogic.decBooster(BoosterTypeEnum.needle);
        }
        this.settings.balls = this.gameLogic.getBoosterCount(BoosterTypeEnum.yarn);
        for (let i = 0; i < this.settings.balls - 1; i++) {
            this.gameLogic.decBooster(BoosterTypeEnum.yarn);
        }
        this.settings.boosterBallPartOfPictureWidth = this.$model.boosterBallPartOfPictureWidth;
        this.$model.boosterBallPartOfPictureWidth = .3;
    }

    private resetFTUEModelSettings(): void {
        this.$model.boosterBallPartOfPictureWidth = this.settings.boosterBallPartOfPictureWidth;
    }

    private setupPaletteLockers() {
        while (this.paletteLockers.length > 0) {
            this.display.removeChild(this.paletteLockers.shift());
        }

        const size = PALETTE_ITEM_HIT_AREA;
        const palette = this.$model.palette;
        for (let i = 0; i < palette.length; i++) {
            const sprite = new Sprite(Texture.EMPTY);
            sprite.width = size;
            sprite.height = size;
            this.display.addChild(sprite);

            this.paletteLockers.push(sprite);
        }
    }

    private setupSkipButton() {
        return;
        // INFO: Disable button in ftue by Alexey Chernyh
        // this.addChildController({
        //     controller: ButtonController,
        //     animationContent: isMobile() ? AnimationContents.GAME_SCREEN_MOBILE_JSON : AnimationContents.GAME_SCREEN_JSON,
        //     libraryName: 'ftue_btn',
        //
        //     options: <IButtonControllerOptions>{
        //         label: LocalizationContents.getText('Skip'),
        //         style: GreenButtonTextStyle,
        //         onClick: (status: boolean) => {
        //             this.modelEvents.dispatch(this.model, GameEvent.tutorialButtonClick, <GameEventSceneButtonClick>{ buttonName: 'Skip', status });
        //
        //             if (!status) {
        //                 return;
        //             }
        //
        //             this.saveManager.saveTutorialStep(-1);
        //             this.resetFTUEModelSettings();
        //             this.gameLogic.setState(GameStateEnum.main);
        //         }
        //     }
        // }, 'SkipButton');
    }

    private lockBallBooster(value: boolean) {
        this.btnBallLocker.interactive = value;
    }

    private lockNeedleBooster(value: boolean) {
        this.btnNeedleLocker.interactive = value;
    }

    private lockBucketBooster(value: boolean) {
        this.btnBucketLocker.interactive = value;
        this.btns_booster.getDisplayObjectByQuery('btn_bucket')[0].visible = !value;
    }

    private lockPaletteColors() {
        for (let i = 0; i < this.paletteLockers.length; i++) {
            this.paletteLockers[i].interactive = true;
        }
    }

    private unlockPaletteIndex(index = -1) {
        for (let i = 0; i < this.paletteLockers.length; i++) {
            this.paletteLockers[i].interactive = index > -1 ? i !== index : false;
        }
    }

    private showOverlay(blackout: boolean) {
        this.hideOverlay();

        const overlay = new Graphics();
        overlay.name = 'overlay';
        overlay.alpha = 0;
        overlay.interactive = true;
        this.display.addChildAt(overlay, 1);
        if (this.display.getChildIndex(overlay) > this.display.getChildIndex(this.btns_booster.display)) {
            this.display.swapChildren(overlay, this.btns_booster.display);
        }
        gsap.killTweensOf(overlay);
        if (blackout) {
            gsap.to(overlay, this.overlayShowingTime, { alpha: 1 });
        }

        this.updateOverlay();
    }

    private hideOverlay() {
        const overlay = this.display.getChildByName('overlay');
        if (overlay) {
            this.display.filters = [];

            overlay.interactive = false;

            gsap.killTweensOf(overlay);
            gsap.to(overlay, this.overlayShowingTime, {
                alpha: 0, onComplete: () => {
                    if (this.display.children.includes(overlay)) {
                        this.display.removeChild(overlay);
                    }
                    this.onResize(this._width, this._height, this._scale);
                },
            });
        }
    }

    private timeout(value = 1000): Promise<any> {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(this);
            }, value);
        });
    }

    private step01_showPrompt(): Promise<any> {
        this.showOverlay(true);
        this.lockNeedleBooster(true);
        this.lockBallBooster(true);
        this.lockBucketBooster(true);
        this.lockPaletteColors();

        this.modelEvents.dispatch(this.$model, GameEvent.tutorialPopupOpen, { popupName: 'Tutorial 1' });

        return new Promise((resolve) => {
            this.addChildControllerAsync({
                controller: FTUEPopupMainController,
                options: <IFTUEPopupMainControllerOptions>{
                    onClick: (status: boolean) => {
                        this.modelEvents.dispatch(this.model, GameEvent.tutorialButtonClick, <GameEventSceneButtonClick>{ buttonName: GameEventPopupButtonName.next, status});

                        if (!status) {
                            return;
                        }

                        const popup = this.$childControllers.get('FTUEPopupStart');
                        this.removeChildController(popup);
                        resolve(true);
                    }
                }
            }, false, 'FTUEPopupStart');
        });
    }

    private step02_pointJoistick(): Promise<any> {
        this.showOverlay(true);
        this.lockNeedleBooster(true);
        this.lockBallBooster(true);
        this.lockBucketBooster(true);
        this.unlockPaletteIndex(0);

        this.modelEvents.dispatch(this.$model, GameEvent.tutorialPopupOpen, { popupName: 'Tutorial 2' });

        this.addChildController({
            controller: FTUEPopupController,
            options: <IFTUEPopupControllerOptions>{
                text: LocalizationContents.getText('embroidery scheme')
            }
        }, 'FTUEPopupColor');

        this.addChildController({
            controller: FTUEArrowController,
        }, 'FTUEArrow');

        this.updatePopupPosition();

        const popup = this.$childControllers.get('FTUEPopupColor');
        const arrow = this.$childControllers.get('FTUEArrow');


        const item = this.paletteController.$childControllers.get('item_palet_hand');
        const filterGlow = new filter.GlowFilter(50, 0, 0);
        item.display.filters = [
            filterGlow
        ];

        gsap.killTweensOf(filterGlow);
        gsap.to(filterGlow, {
            duration: .5,
            innerStrength: 3,
            yoyo: true,
            yoyoEase: true,
            repeat: -1,
        });

        return new Promise((resolve, reject) => {
            const handler = () => {
                if (this.isDestroyed) {
                    reject();
                    return;
                }

                item.display.filters = [];
                gsap.killTweensOf(filterGlow);

                this.modelEvents.off(this.$model, 'setColor', handler);
                this.removeChildController(popup);
                this.removeChildController(arrow);
                resolve(true);
            }
            this.modelEvents.on(this.$model, 'setColor', handler, this);
        });
    }

    private step03_pointColor(): Promise<any> {
        const index = 4;
        this.unlockPaletteIndex(index);
        this.lockNeedleBooster(true);
        this.lockBallBooster(true);
        this.showOverlay(true);

        this.modelEvents.dispatch(this.$model, GameEvent.tutorialPopupOpen, { popupName: 'Tutorial 3' });

        this.addChildController({
            controller: FTUEPopupController,
            options: <IFTUEPopupControllerOptions>{
                text: LocalizationContents.getText('select a thread of First Color')
            }
        }, 'FTUEPopupColor');

        this.addChildController({
            controller: FTUEArrowController,
        }, 'FTUEArrow');

        this.updatePopupPosition();

        const item = (this.paletteController.$childControllers.get('list') as PaletteListController).items[4];
        const filterGlow = new filter.GlowFilter(50, 0, 0);
        item.display.filters = [
            filterGlow
        ];

        gsap.killTweensOf(filterGlow);
        gsap.to(filterGlow, {
            duration: .5,
            innerStrength: 3,
            yoyo: true,
            yoyoEase: true,
            repeat: -1,
        });

        const popup = this.$childControllers.get('FTUEPopupColor');
        const arrow = this.$childControllers.get('FTUEArrow');
        return new Promise((resolve, reject) => {
            const handler = () => {
                if (this.isDestroyed) {
                    reject();
                    return;
                }

                item.display.filters = [];
                gsap.killTweensOf(filterGlow);

                this.modelEvents.off(this.$model, 'setColor', handler);
                this.removeChildController(popup);
                this.removeChildController(arrow);
                this.lockPaletteColors();
                resolve(true);
            }
            this.modelEvents.on(this.$model, 'setColor', handler, this);
        });
    }

    private step04_waitColored(): Promise<any> {
        this.hideOverlay();
        this.lockNeedleBooster(true);
        this.lockBallBooster(true);
        this.lockBucketBooster(true);
        this.lockPaletteColors();

        return new Promise((resolve, reject) => {
            const handler = () => {
                if (this.isDestroyed) {
                    reject();
                    return;
                }

                this.modelEvents.off(this.$model, 'doneColor', handler);
                this.showOverlay(false);
                this.gameLogic.setColor(1);
                resolve(true);
            }
            this.modelEvents.on(this.$model, 'doneColor', handler, this);
        });
    }

    private step05_pointBoosterLine(): Promise<any> {
        this.gameLogic.setColor(1);
        this.lockNeedleBooster(false);
        this.showOverlay(true);
        this.lockBallBooster(true);
        this.lockPaletteColors();

        this.modelEvents.dispatch(this.$model, GameEvent.tutorialPopupOpen, { popupName: 'Tutorial 4' });

        this.addChildController({
            controller: FTUEPopupController,
            options: <IFTUEPopupControllerOptions>{
                text: LocalizationContents.getText('Needle tool description')
            }
        }, 'FTUEPopupColor');

        this.addChildController({
            controller: FTUEArrowController,
        }, 'FTUEArrow');

        this.updatePopupPosition();

        const btnNeedle = this.btns_booster.$childControllers.get('btn_needle') as BoosterListItemController;
        const filterGlow = new filter.GlowFilter(50, 0, 0);
        btnNeedle.display.filters = [
            filterGlow
        ];

        gsap.killTweensOf(filterGlow);
        gsap.to(filterGlow, {
            duration: .5,
            innerStrength: 3,
            yoyo: true,
            yoyoEase: true,
            repeat: -1,
        });

        const popup = this.$childControllers.get('FTUEPopupColor');
        const arrow = this.$childControllers.get('FTUEArrow');
        return new Promise((resolve, reject) => {
            const handler = () => {
                if (this.isDestroyed) {
                    reject();
                    return;
                }
                btnNeedle.display.filters = [];
                gsap.killTweensOf(filterGlow);

                this.modelEvents.off(this.$model, 'setBoosterType', handler);
                this.removeChildController(popup);
                this.removeChildController(arrow);
                resolve(true);
            }
            this.modelEvents.on(this.$model, 'setBoosterType', handler, this);
        });
    }

    private step06_waitBoosterLine(): Promise<any> {
        this.hideOverlay();
        this.lockNeedleBooster(true);
        this.lockBallBooster(true);
        this.lockBucketBooster(true);
        this.lockPaletteColors();

        return new Promise((resolve, reject) => {
            const handler = () => {
                if (this.isDestroyed) {
                    reject();
                    return;
                }

                this.showOverlay(false);
                this.modelEvents.off(this.$model, 'progressColor', handler);
                resolve(true);
            }
            this.modelEvents.on(this.$model, 'progressColor', handler, this);
        });
    }

    private step07_pointBoosterBall(): Promise<any> {
        this.showOverlay(true);
        this.lockNeedleBooster(true);
        this.lockBallBooster(false);
        this.lockBucketBooster(true);
        this.lockPaletteColors();

        this.modelEvents.dispatch(this.$model, GameEvent.tutorialPopupOpen, { popupName: 'Tutorial 5' });

        this.addChildController({
            controller: FTUEPopupController,
            options: <IFTUEPopupControllerOptions>{
                text: LocalizationContents.getText('Ball tool description')
            }
        }, 'FTUEPopupColor');

        this.addChildController({
            controller: FTUEArrowController,
        }, 'FTUEArrow');

        this.updatePopupPosition();

        const btnBall = this.btns_booster.$childControllers.get('btn_ball') as BoosterListItemController;
        const filterGlow = new filter.GlowFilter(50, 0, 0);
        btnBall.display.filters = [
            filterGlow
        ];

        gsap.killTweensOf(filterGlow);
        gsap.to(filterGlow, {
            duration: .5,
            innerStrength: 3,
            yoyo: true,
            yoyoEase: true,
            repeat: -1,
        });

        const popup = this.$childControllers.get('FTUEPopupColor');
        const arrow = this.$childControllers.get('FTUEArrow');
        return new Promise((resolve, reject) => {
            const handler = () => {
                if (this.isDestroyed) {
                    reject();
                    return;
                }

                gsap.killTweensOf(filterGlow);
                btnBall.display.filters = [];

                this.modelEvents.off(this.$model, 'setBoosterType', handler);
                this.removeChildController(popup);
                this.removeChildController(arrow);
                this.lockNeedleBooster(true);
                this.lockBallBooster(true);
                resolve(true);
            }
            this.modelEvents.on(this.$model, 'setBoosterType', handler, this);
        });
    }

    private step08_waitBoosterBall(): Promise<any> {
        this.hideOverlay();
        this.lockBallBooster(true);
        this.lockNeedleBooster(true);
        this.lockBucketBooster(true);
        this.lockPaletteColors();

        return new Promise((resolve, reject) => {
            const handler = () => {
                if (this.isDestroyed) {
                    reject();
                    return;
                }

                this.showOverlay(false);
                this.modelEvents.off(this.$model, 'progressColor', handler);
                resolve(true);
            }
            this.modelEvents.on(this.$model, 'progressColor', handler, this);
        });
    }

    private step09_showLastPopup(): Promise<any> {
        this.showOverlay(true);
        this.lockNeedleBooster(true);
        this.lockBallBooster(true);
        this.lockBucketBooster(true);
        this.lockPaletteColors();

        this.modelEvents.dispatch(this.$model, GameEvent.tutorialPopupOpen, { popupName: 'Tutorial 6' });

        return new Promise((resolve, reject) => {
            this.addChildControllerAsync({
                controller: FTUEPopupEndController,
                options: <IFTUEPopupMainControllerOptions>{
                    onClick: (status: boolean) => {
                        this.modelEvents.dispatch(this.model, GameEvent.tutorialButtonClick, <GameEventSceneButtonClick>{ buttonName: 'Next', status});

                        if (!status) {
                            return;
                        }

                        const popup = this.$childControllers.get('FTUEPopupEndController');
                        if (!popup) {
                            if (this.isDestroyed) {
                                reject();
                                return;
                            }
                        }
                        this.removeChildController(popup);
                        resolve(true);
                    }
                }
            }, false, 'FTUEPopupEndController');
        });
    }

    private step10_waitPaintEnding(): Promise<any> {
        this.hideOverlay();
        this.lockNeedleBooster(true);
        this.lockBallBooster(true);
        this.lockBucketBooster(true);
        this.unlockPaletteIndex();
        this.gameLogic.setInstrumentType(InstrumentTypeEnum.paint);

        return new Promise((resolve, reject) => {
            const handleSetState = () => {
                if (this.isDestroyed) {
                    reject();
                    return;
                }
                this.modelEvents.off(this.$model, 'setState', handleSetState);
                this.modelEvents.off(this.$model, 'fillComplete', fillComplete);

                this.hideOverlay();
                this.resetFTUEModelSettings();

                this.gameLogic.setState(GameStateEnum.main);
                resolve(true);
            }

            const fillComplete = () => {
                if (this.isDestroyed) {
                    reject();
                    return;
                }
                this.modelEvents.off(this.$model, 'setState', handleSetState);
                this.modelEvents.off(this.$model, 'fillComplete', fillComplete);

                this.hideOverlay();
                this.resetFTUEModelSettings();

                this.saveManager.saveTutorialStep(-1);

                resolve(true);
            }
            this.modelEvents.on(this.$model, 'setState', handleSetState, this);
            this.modelEvents.on(this.$model, 'fillComplete', fillComplete, this);
        });
    }

    private updateOverlay() {
        const overlay = this.display.getChildByName('overlay') as Graphics;
        if (overlay) {
            overlay.clear();
            overlay.beginFill(0x000000, 0.75);
            overlay.drawRect(-this._width / 2 / this._scale * 2, -this._height / 2 / this._scale * 2, this._width / this._scale * 2, this._height / this._scale * 2);
            overlay.endFill();
        }
    }

    private updatePopupPosition() {
        let level = this.saveManager.getSaveGameData().tutorialStep === undefined ? 0 : this.saveManager.getSaveGameData().tutorialStep;

        const popup = this.$childControllers.get('FTUEPopupColor');
        const arrow = this.$childControllers.get('FTUEArrow');
        const multiplierY = isMobile() ? -0.35 : 1;
        const multiplierX = isMobile() ? 1.4 : 1;
        switch (level) {
            case 1: // step02_pointJoistick
                {
                    const palettePoint = this.paletteLockers[0];
                    if (arrow) {
                        arrow.display.position.set(palettePoint.x + palettePoint.width / 3, palettePoint.y - palettePoint.height / 2);
                    }
                    if (popup) {
                        popup.display.position.set(palettePoint.x + popup.display.width / 2.4 * multiplierX, palettePoint.y - (popup.display.height * multiplierY))
                    }
                }
            break;
            case 2: // step03_pointColor
                {const palettePoint = this.paletteLockers[4];
                if (arrow) {
                    arrow.display.position.set(palettePoint.x + palettePoint.width / 3, palettePoint.y - palettePoint.height / 2);
                    if (isMobile()) {
                        arrow.display.position.y += 70;
                    }
                }
                if (popup) {
                    if (isMobile()) {
                        popup.display.position.set(palettePoint.x + popup.display.width / 2.4 * multiplierX, palettePoint.y - (popup.display.height * multiplierY))
                    } else {
                        popup.display.position.set(palettePoint.x + popup.display.width / 2.4 * multiplierX - 250, palettePoint.y - (popup.display.height * multiplierY))
                    }
                }}
            break;
            case 5: // step05_pointBoosterLine
                {if (popup) {
                    popup.display.position.set(
                        this.btnNeedleLocker.x - popup.display.width * .7,
                        this.btnNeedleLocker.y + popup.display.height / 2
                    );
                }
                if (arrow) {
                    arrow.display.rotation = -Math.PI / 2;
                    arrow.display.position.set(
                        this.btnNeedleLocker.x - arrow.display.height / 2,
                        this.btnNeedleLocker.y + arrow.display.width / 2
                    );
                }}
            break;
            case 7: // step07_pointBoosterBall
                {if (popup) {
                    popup.display.position.set(
                        this.btnBallLocker.x - popup.display.width * .7,
                        this.btnBallLocker.y + popup.display.height / 2
                    );
                }
                if (arrow) {
                    arrow.display.rotation = -Math.PI / 2;
                    arrow.display.position.set(
                        this.btnBallLocker.x - arrow.display.height / 2,
                        this.btnBallLocker.y + arrow.display.width / 2
                    );
                }}
            break
        }
    }
}
